<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: null,
                    },
                ]"
            >
                <template v-slot:corner>
                    <div class="flex gap-x-1 items-center">
                        <BadgePill>
                            <inertia-link :href="meta.links.stats">{{ $t('messages.stats') }}</inertia-link>
                        </BadgePill>
                        <BadgePill>
                            <inertia-link :href="meta.links.usage_index">
                                {{ $t('messages.usage_information') }}
                            </inertia-link>
                        </BadgePill>
                    </div>
                </template>
            </SmartBreadcrumbs>
        </template>

        <div class="w-full flex flex-wrap gap-2 items-center justify-end">
            <AppButton element="InertiaLink" :href="gamificationSet.links.rewards_index">
                <IconAward class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.gamification_rewards', 2) }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.items_index">
                <Icon3dCubeSphere class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.gamification_items', 2) }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.card_collections_index">
                <IconCards class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.card_collections', 2) }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.badges_edit">
                <IconBadge class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.badges', 2) }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.battles_index">
                <IconHeartHandshake class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.battles', 2) }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.fetch_quests_index">
                <IconComponents class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.fetch_quests', 2) }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.chests_index">
                <IconBrandCodesandbox class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.gamification_chests', 2) }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.settings_edit">
                <IconSettings class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.settings', 2) }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.actions_index">
                <IconDeviceDesktopAnalytics class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.actions', 2) }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.health_max_edit">
                <IconHealthRecognition class="mr-1 text-gray-400 h-4 w-4" />
                {{ $t('messages.health_amount') }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.announcements_index">
                <IconNews class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.announcements', 2) }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.itineraries_index">
                <IconMap class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.itineraries', 2) }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.character_types_index">
                <IconId class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.gamification_character_types', 2) }}
            </AppButton>
            <AppButton element="InertiaLink" :href="gamificationSet.links.schedule_edit">
                <IconCalendarTime class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.schedules', 2) }}
            </AppButton>
            <AppButton
                element="InertiaLink"
                :href="gamificationSet.links.quests_index"
                v-if="gamificationSet.links.quests_index"
            >
                <IconTransferVertical class="mr-1 text-gray-400 h-4 w-4" />
                {{ $tc('messages.quests', 2) }}
            </AppButton>
        </div>

        <TitleFieldsFormSection v-if="meta.info.import_progress">
            <div>
                {{ $t('messages.importing') }}
            </div>
            <div>
                <ImportProgress :import-progress="meta.info.import_progress" />
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $t('messages.information') }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-6">
                    <FormLabel :value="$t('messages.grade_level')" for="grade_level_ids" />
                    <div class="mt-1">
                        <label v-for="grade_level in gamificationSet.grade_levels" class="mr-1" :key="grade_level.id">
                            {{ grade_level.name }}
                        </label>
                    </div>
                </div>
                <div class="sm:col-span-6">
                    <FormLabel :value="$tc('messages.groups', 2)" />
                    <div class="mt-1">
                        {{ gamificationSet.group_names }}
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $tc('messages.levels', 2) }}
            </template>
            <details class="mt-3 sm:col-span-12">
                <summary>
                    <h2 class="inline-block text-lg leading-6 font-medium text-gray-900 mb-3">
                        {{ $tc('messages.levels', 2) }}
                    </h2>
                </summary>

                <div class="flex flex-col mt-5">
                    <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                        <div
                            class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                        >
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.numbers', 1) }}
                                        </th>
                                        <th
                                            class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.experience_points', 2) }}
                                        </th>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.ranks', 1) }}
                                        </th>
                                        <th
                                            class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                        >
                                            {{ $tc('messages.students', 2) }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr
                                        :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                        v-for="(level, index) in gamificationSet.levels"
                                        :key="`levels_${index}`"
                                    >
                                        <td
                                            class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                        >
                                            {{ index }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                            {{ level.width }} [{{ level.min }} - {{ level.max }})
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                            {{ level.rank }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                            <span
                                                :class="{
                                                    'text-gray-300': level.gamification_score_cards_count === 0,
                                                }"
                                            >
                                                {{ level.gamification_score_cards_count }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </details>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection v-if="missingGroups.length > 0">
            <template v-slot:title>
                {{ $tc('messages.groups', 2) }}
            </template>
            <div>
                {{ $t('messages.include') }}
            </div>
            <div v-for="group in missingGroups" :key="group.id">
                {{ group.name }}

                <InertiaSquareConfirmActionButton
                    :confirm-text="$t('messages.include')"
                    :title="$t('messages.are_you_sure?')"
                    :href="group.url"
                    method="patch"
                >
                    <template v-slot:icon>
                        <svg viewBox="0 0 20 20" fill="currentColor" class="pencil w-4 h-4">
                            <path
                                fill-rule="evenodd"
                                d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.415 1.415l.707-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z"
                                clip-rule="evenodd"
                            ></path>
                        </svg>
                    </template>
                </InertiaSquareConfirmActionButton>
            </div>
        </TitleFieldsFormSection>
    </PanelPage>
</template>

<script>
import BadgePill from '@/components/BadgePill.vue';

import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import InertiaLinkButton from '@/components/InertiaLinkButton.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import {
    Icon3dCubeSphere,
    IconAward,
    IconBadge,
    IconBrandCodesandbox,
    IconCalendarTime,
    IconCards,
    IconComponents,
    IconDeviceDesktopAnalytics,
    IconHealthRecognition,
    IconHeartHandshake,
    IconId,
    IconMap,
    IconNews,
    IconSettings,
    IconTransferVertical,
} from '@tabler/icons-vue';
import ImportProgress from '@/components/ImportProgress.vue';
import AppButton from '../../../../components/AppButton.vue';

export default {
    components: {
        AppButton,
        ImportProgress,
        PanelPage,
        TitleFieldsFormSection,
        FormLabel,
        BadgePill,
        InertiaLinkButton,
        InertiaSquareConfirmActionButton,
        IconAward,
        Icon3dCubeSphere,
        IconCards,
        IconBadge,
        IconHeartHandshake,
        IconComponents,
        IconBrandCodesandbox,
        IconSettings,
        IconDeviceDesktopAnalytics,
        IconHealthRecognition,
        IconNews,
        IconMap,
        IconId,
        IconCalendarTime,
        IconTransferVertical,
    },
    props: ['meta', 'gamificationSet', 'missingGroups'],
    layout: Layout,
    data() {
        return {};
    },
    name: 'Show',
    computed: {},
    methods: {},
    mounted() {},
};
</script>
